import { useState, useEffect } from 'react'
import { Tooltip, Button, Upload } from 'antd'
import { RcFile } from 'antd/lib/upload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faImage } from '@fortawesome/pro-solid-svg-icons/faImage'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faClose } from '@fortawesome/pro-solid-svg-icons/faClose'
import { useTranslation } from 'react-i18next'
import {
  Wrapper,
  Container,
  Content,
  RemoveImage,
  Image,
  Title,
  Subtitle,
  IconWrapper,
} from './styled'

interface IProps {
  image: File | string | null
  setImage: (value: File | null) => void
  translatePrefix?: string
  fullWidth?: boolean
  hasError?: boolean
  height?: string
  imageFit?: 'cover' | 'contain'
  imageBackgroundColor?: string
  accept?: string
  validateFile?: (file: File) => boolean
  onInvalidFileType?: () => void
}

const UploadImage = ({
  image,
  setImage,
  translatePrefix = 'administration.certificate.new.backgroundImage',
  fullWidth = false,
  hasError = false,
  height,
  imageFit = 'cover',
  imageBackgroundColor = '#ccc',
  accept = '.jpg, .jpeg, .png',
  validateFile,
  onInvalidFileType,
}: IProps) => {
  const { t } = useTranslation()
  const [file, setFile] = useState<string | null>(null)

  useEffect(() => {
    if (image === null) {
      setFile(null)
    } else {
      if (typeof image === 'string') {
        setFile(image)
      } else {
        setFile(URL.createObjectURL(image))
      }
    }
  }, [image])

  const beforeUpload = (newFile: RcFile) => {
    if (validateFile && !validateFile(newFile)) {
      onInvalidFileType?.()
      return false
    }

    setImage(newFile)
    return false
  }

  const uploadIcon = fullWidth ? faPlus : faImage

  return (
    <Wrapper $fullWidth={fullWidth} $height={height}>
      {file ? (
        <Container
          imageloaded="true"
          $fullWidth={fullWidth}
          $hasError={hasError}
        >
          <RemoveImage>
            <Tooltip
              title={t(`${translatePrefix}.removeImage`)}
              placement="bottom"
            >
              <FontAwesomeIcon icon={faClose} onClick={() => setImage(null)} />
            </Tooltip>
          </RemoveImage>
          <Image
            url={file}
            $imageFit={imageFit}
            $backgroundColor={imageBackgroundColor}
          />
        </Container>
      ) : (
        <Upload
          beforeUpload={(file: RcFile) => beforeUpload(file)}
          accept={accept}
          showUploadList={false}
        >
          <Container
            imageloaded="false"
            $fullWidth={fullWidth}
            $hasError={hasError}
          >
            <Content $fullWidth={fullWidth}>
              <IconWrapper $fullWidth={fullWidth}>
                <FontAwesomeIcon icon={uploadIcon} />
              </IconWrapper>
              <Title $fullWidth={fullWidth}>
                {t(`${translatePrefix}.allowedFormats`)}
              </Title>
              {t(`${translatePrefix}.idealSize`, { defaultValue: '' }) && (
                <Subtitle>{t(`${translatePrefix}.idealSize`)}</Subtitle>
              )}
              <Button type="link">{t(`${translatePrefix}.uploadImage`)}</Button>
            </Content>
          </Container>
        </Upload>
      )}
    </Wrapper>
  )
}

export default UploadImage
